import ApimApiClient from '@/services/serviceClients/apimApiClient';
import EpiApiClient from "@/services/serviceClients/epiApiClient";
import {
  PROPERTY_REPORT_LIST_API_PATH,
  PROPERTY_REPORT_OWNER_LOOKUP_API_PATH,
  PROPERTY_REPORT_CREATE_API_PATH,
  PROPERTY_REPORT_FREE_TRIALS_API_PATH,
  PROPERTY_REPORT_DETAILS_API_PATH,
  PROPERTY_REPORT_DOWNLOAD_API_PATH,
  PROPERTY_REPORT_UPDATE_API_PATH,
  PROPERTY_REPORT_UPDATE_TRIAL_CREDITS_API_PATH,
  PROPERTY_REPORT_PAYMENT_RETRY_API_PATH,
  PROPERTY_REPORT_PAYMENT_RETRY_ELIGIBILITY_API_PATH,
  BRANCH_SHIP_TO_ADDRESSES_API_PATH
} from '@/config/constants';

class propertyReportsService {

  static async list(payload) {
    try {
      const response = await ApimApiClient.post(`${PROPERTY_REPORT_LIST_API_PATH}`, payload);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: propertyReportsService._err(err) };
    }
  }
  
  static async lookupOwner(addressPayload) {
    try {
      const response = await ApimApiClient.post(`${PROPERTY_REPORT_OWNER_LOOKUP_API_PATH}`, addressPayload);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: propertyReportsService._err(err) };
    }
  }
  
  static async create(orderPayload) {
    try {
      const response = await ApimApiClient.post(`${PROPERTY_REPORT_CREATE_API_PATH}`, orderPayload);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: propertyReportsService._err(err) };
    }
  }
  
  static async freeTrials(customerCode) {
    try {
      const normalizedCustomerCode = customerCode?.trim();

      if (!normalizedCustomerCode) {
        return { success: false, error: 'customerCode is required' };
      }

      const params = { customerCode: normalizedCustomerCode };
      const response = await ApimApiClient.get(`${PROPERTY_REPORT_FREE_TRIALS_API_PATH}`, {
        params
      });
      const data = response.data || {};
      const results = Array.isArray(data.Results) ? data.Results : [];

      if (data.FreeTrialsRemaining === undefined) {
        const normalizedLookupCode = normalizedCustomerCode.toUpperCase();
        const match = results.find(item => ((item?.CustomerCode || '').toUpperCase() === normalizedLookupCode)) || results[0];
        data.FreeTrialsRemaining = match?.FreeTrialsRemaining ?? 0;
      }

      return { success: true, data };
    } catch (err) {
      return { success: false, error: propertyReportsService._err(err) };
    }
  }

  static async updateTrialCredits(customerCode, credits, userId) {
    try {
      const payload = {
        CustomerCode: customerCode,
        Credits: credits,
        CorrelationId: propertyReportsService._generateGuid()
      };

      if (userId) {
        payload.UserId = userId;
      }

      const response = await ApimApiClient.post(`${PROPERTY_REPORT_UPDATE_TRIAL_CREDITS_API_PATH}`, payload);

      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: propertyReportsService._err(err) };
    }
  }

  static async logCreditAdjustment(logRequest) {
    try {
      const response = await EpiApiClient.post(`/api/services/HomeownerInsights/LogCreditAdjustment`, logRequest);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: propertyReportsService._err(err) };
    }
  }

  static generateGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // Backward compatibility alias
  static _generateGuid() {
    return propertyReportsService.generateGuid();
  }

  static async getBranchShipToMapping() {
    try {
      const response = await EpiApiClient.get(BRANCH_SHIP_TO_ADDRESSES_API_PATH);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: propertyReportsService._err(err) };
    }
  }

  /**
   * Alias for lookupOwner - validates address and gets owner info
   */
  static async validateAddress(addressPayload) {
    return propertyReportsService.lookupOwner(addressPayload);
  }

  /**
   * Check if property report already exists for an address
   */
  static async checkExistingReport(addressPayload) {
    try {
      if (!addressPayload) {
        return { 
          success: false, 
          error: 'Address payload is required',
          data: null
        };
      }

      const response = await ApimApiClient.post(PROPERTY_REPORT_OWNER_LOOKUP_API_PATH, addressPayload);
      
      const data = response.data || {};
      
      return { 
        success: true, 
        data: {
          exists: data.ExistingReportId ? true : false,
          reportId: data.ExistingReportId || null,
          reportDate: data.ExistingReportDate || null
        },
        error: null 
      };
    } catch (err) {
      return { 
        success: false, 
        error: propertyReportsService._err(err),
        data: null
      };
    }
  }

  /**
   * Get price per property report
   */
  static async getPricePerReport() {
    try {
      // Price configuration - can be fetched from API or config
      // For now returning standard pricing
      return { 
        success: true, 
        data: {
          pricePerReport: 99,
          currency: 'USD',
          uom: 'report'
        },
        error: null 
      };
    } catch (err) {
      return { 
        success: false, 
        error: propertyReportsService._err(err),
        data: null
      };
    }
  }

  static async details(reportId) {
    try {
      const response = await ApimApiClient.get(`${PROPERTY_REPORT_DETAILS_API_PATH}/${reportId}`, {
        params: {
          correlationId: propertyReportsService.generateGuid()
        }
      });
      return { success: true, data: response.data, response };
    } catch (err) {
      console.error('propertyReportsService.details failed:', err);
      return { success: false, error: propertyReportsService._err(err) };
    }
  }

  /**
   * Map property report detail response to frontend structure
   * @param {Object} src - Source data from API
   * @returns {Object} Mapped property report detail
   */
  static mapDetailResponse(src) {
    if (!src) return null;
    
    return {
      overview: {
        status: src.Overview?.Status,
        leadQuality: src.Overview?.LeadQuality,
        upsellScore: src.Overview?.UpsellScore,
        streetNumber: src.Overview?.StreetNumber,
        streetAddress: src.Overview?.StreetAddress,
        city: src.Overview?.City,
        state: src.Overview?.State,
        zip: src.Overview?.Zip,
        latitude: src.Overview?.Latitude,
        longitude: src.Overview?.Longitude,
        salesRepresentativeFullName: src.Overview?.SalesRepresentativeFullName
      },
      ownersOriginalInformation: {
        originalPrimaryOwnerFirstName: src.OwnersOriginalInformation?.OriginalPrimaryOwnerFirstName,
        originalPrimaryOwnerLastName: src.OwnersOriginalInformation?.OriginalPrimaryOwnerLastName,
        originalSecondaryOwnerFirstName: src.OwnersOriginalInformation?.OriginalSecondaryOwnerFirstName,
        originalSecondaryOwnerLastName: src.OwnersOriginalInformation?.OriginalSecondaryOwnerLastName
      },
      customerContactDetails: {
        primaryOwnerFirstName: src.CustomerContactDetails?.PrimaryOwnerFirstName,
        primaryOwnerLastName: src.CustomerContactDetails?.PrimaryOwnerLastName,
        primaryOwnerPhoneNumber: src.CustomerContactDetails?.PrimaryOwnerPhoneNumber,
        primaryOwnerEmailAddress: src.CustomerContactDetails?.PrimaryOwnerEmailAddress,
        secondaryOwnerFirstName: src.CustomerContactDetails?.SecondaryOwnerFirstName,
        secondaryOwnerLastName: src.CustomerContactDetails?.SecondaryOwnerLastName,
        secondaryOwnerPhoneNumber: src.CustomerContactDetails?.SecondaryOwnerPhoneNumber,
        secondaryOwnerEmailAddress: src.CustomerContactDetails?.SecondaryOwnerEmailAddress
      },
      homeDetails: {
        classification: src.HomeDetails?.Classification,
        county: src.HomeDetails?.County,
        subdivision: src.HomeDetails?.Subdivision,
        schoolDistrict: src.HomeDetails?.SchoolDistrict,
        yearHomeBuilt: src.HomeDetails?.YearHomeBuilt,
        yearsInHome: src.HomeDetails?.YearsInHome,
        ownerOccupied: src.HomeDetails?.OwnerOccupied,
        stories: src.HomeDetails?.Stories,
        style: src.HomeDetails?.Style,
        construction: src.HomeDetails?.Construction
      },
      buyerPersonas: (src.BuyerPersonas || []).map((p) => ({
        trait: p.trait,
        likelihood: p.likelihood,
        closingTips: ""
      }))
    };
  }

  static async saveOwnerInformation(reportId, ownerInformationRequest) {
    try {
      const response = await ApimApiClient.put(`${PROPERTY_REPORT_UPDATE_API_PATH}/${reportId}`, ownerInformationRequest, {
        params: {
          correlationId: propertyReportsService.generateGuid()
        }
      });
      return { success: true, data: response.data, response };
    } catch (error) {
      console.error('An error occurred saving owner information', error);
      return { success: false, error: propertyReportsService._err(error) };
    }
  }

  static async download(reportId) {
    try {
      const response = await ApimApiClient.get(`${PROPERTY_REPORT_DOWNLOAD_API_PATH}/${reportId}`, {
        responseType: 'blob',
        params: {
          correlationId: propertyReportsService.generateGuid()
        }
      });
      return { success: true, data: response.data, response };
    } catch (err) {
      console.error('propertyReportsService.download failed:', err);
      return { success: false, error: propertyReportsService._err(err) };
    }
  }

  // Returns the subset of reportIds that can still be retried (no retry logged yet).
  static async getRetryEligibility(reportIds) {
    try {
      const response = await ApimApiClient.post(PROPERTY_REPORT_PAYMENT_RETRY_ELIGIBILITY_API_PATH, { ReportIds: reportIds });
      const data = response.data?.data ?? response.data?.Data ?? response.data;
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (err) {
      return { success: false, data: [], error: propertyReportsService._err(err) };
    }
  }

  static async logPaymentRetry(retry) {
    try {
      const response = await ApimApiClient.post(PROPERTY_REPORT_PAYMENT_RETRY_API_PATH, {
        PropertyReportId: retry.reportId,
        TransactionSetupId: retry.transactionSetupId || null,
        PaymentAccountId: retry.paymentAccountId || null,
        Outcome: retry.outcome,
        ErrorMessage: retry.errorMessage || null
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: propertyReportsService._err(err) };
    }
  }

  static downloadAndHandle(blob, reportId) {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `property-report-${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      console.error('Failed to handle download:', err);
      return false;
    }
  }

  static _err(err) {
    if (!err) return 'unknown error';
    return err.response?.data?.message || err.response?.data?.error_description || err.message || 'request failed';
  }
}
export default propertyReportsService;
